/**
 * AdminRestoreModuleModal — potwierdzenie przywracania modułu z soft-delete (T8.1b).
 *
 * Backend: POST /api/admin/modules/:code/restore (no body). Walidacja: 409 MODULE_NOT_RETIRED
 * jesli moduł aktywny (gcrecord IS NULL), 404 jesli nie istnieje.
 *
 * Po sukcesie: onSuccess callback.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export interface AdminRestoreModuleModalProps {
  open: boolean;
  moduleCode: string;
  moduleName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminRestoreModuleModal({
  open,
  moduleCode,
  moduleName,
  onClose,
  onSuccess,
}: AdminRestoreModuleModalProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const onConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/admin/modules/${moduleCode}/restore`);
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const code = axErr.response?.data?.error ?? null;
      if (code === 'MODULE_NOT_RETIRED') {
        setError(t('admin.modules.restore.errors.notRetired'));
      } else {
        setError(t('admin.modules.restore.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={t('admin.modules.restore.title', { name: moduleName })}
      description={t('admin.modules.restore.description')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.modules.restore.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void onConfirm()}
            loading={submitting}
            disabled={submitting}
          >
            {submitting
              ? t('admin.modules.restore.submitting')
              : t('admin.modules.restore.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-tertiary">{t('admin.modules.restore.body')}</p>
      {error && (
        <div
          role="alert"
          className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
        >
          {error}
        </div>
      )}
    </Modal>
  );
}
